import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { ChartConfigIdType } from '@shared/constants.js'
import Card from 'src/client/components/Card.jsx'
import LocationDetailsTable from 'src/client/components/LocationDetailsTable.jsx'
import LocationMap from 'src/client/components/LocationMap.jsx'
import { useChartData } from 'src/client/hooks/useChartData.js'
import Kpi from 'src/client/components/Kpi.jsx'
import SimpleTable from 'src/client/components/SimpleTable.jsx'
import LineChartCard from 'src/client/components/charts/LineChartCard.jsx'
import MultiLineChartCard from 'src/client/components/charts/MultiLineChartCard.jsx'
import PieChartCard from 'src/client/components/charts/PieChartCard.jsx'
import {
  FootprintImportUploadsTable,
  TestFlightDiagnosticsUploadsTable,
} from 'src/client/components/UploadSampleTables.jsx'
import ClientPurchaseSignalsCard from './ClientPurchaseSignalsCard.jsx'

function KpiCard({ chartId, className }: { chartId: ChartConfigIdType; className?: string }) {
  const { data, loading, error } = useChartData(chartId)
  if (loading) {
    return (
      <Card title={chartId} className={className}>
        loading...
      </Card>
    )
  }
  if (error || !data) {
    return (
      <Card title={chartId} className={className}>
        <span className="text-red-400">加载失败</span>
      </Card>
    )
  }
  return (
    <Card title={data.chartTitle} className={className}>
      <Kpi option={{ dataset: { source: data.rows } }} />
    </Card>
  )
}

function SimpleTableCard({ chartId, className }: { chartId: ChartConfigIdType; className?: string }) {
  const { data, loading, error } = useChartData(chartId)
  if (loading) {
    return (
      <Card title={chartId} className={className}>
        loading...
      </Card>
    )
  }
  if (error || !data) {
    return (
      <Card title={chartId} className={className}>
        <span className="text-red-400">加载失败</span>
      </Card>
    )
  }
  return (
    <Card title={data.chartTitle} className={className}>
      <SimpleTable option={{ dataset: { source: data.rows } }} />
    </Card>
  )
}

function TabButton({ label, isActive, onClick }: { label: string; isActive: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
        isActive
          ? 'border-blue-500 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
      }`}
    >
      {label}
    </button>
  )
}

export default function LocationDashboard() {
  const [searchParams, setSearchParams] = useSearchParams()

  const getActiveTabFromUrl = (): 'data' | 'map' => {
    const tabParam = searchParams.get('tab')
    return tabParam === 'map' ? 'map' : 'data'
  }

  const [activeTab, setActiveTab] = useState<'data' | 'map'>(getActiveTabFromUrl())

  useEffect(() => {
    const tabParam = searchParams.get('tab')
    const newTab = tabParam === 'map' ? 'map' : 'data'
    setActiveTab(newTab)
  }, [searchParams])

  const handleTabChange = (tab: 'data' | 'map') => {
    setActiveTab(tab)
    setSearchParams({ tab })
  }

  return (
    <div className="m-2 flex flex-col gap-2">
      <div className="flex flex-row gap-6">
        <h1 className="text-lg font-bold">位置服务分析</h1>
      </div>

      <ClientPurchaseSignalsCard />

      {/* Tab Navigation */}
      <div className="rounded-t-lg border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-slate-800">
        <nav className="flex space-x-8 px-4">
          <TabButton label="数据分析" isActive={activeTab === 'data'} onClick={() => handleTabChange('data')} />
          <TabButton label="地图分析" isActive={activeTab === 'map'} onClick={() => handleTabChange('map')} />
        </nav>
      </div>

      {/* Tab Content */}
      <div className="rounded-b-lg bg-white dark:bg-slate-800">
        {activeTab === 'data' && (
          <div className="flex flex-col gap-2 p-2">
            {/* KPI 指标行 */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              <KpiCard chartId="location-total-requests" />
              <KpiCard chartId="location-today-requests" />
              <KpiCard chartId="location-success-requests" />
              <KpiCard chartId="location-failed-requests" />
              <KpiCard chartId="location-unique-users" />
            </div>

            {/* ── 外部地图 API 监控 ──
                这一段盯的是钱和配额，和上面那些「业务请求」指标不是一回事：
                上面统计的是 App 打到我们服务端的请求，这里统计的是我们打到高德/Google
                的请求。一次业务请求可能放大成好几次外部调用。

                高德免费额度里 place/around 和 place/detail 都只有 1,000 次/日，撞线时
                返回 HTTP 200 + status:"0"，用户侧只表现为「地点变少了」—— 撞了几个月
                没人发现。这一段就是为了让下次撞线当天可见。 */}
            <div className="mt-2 border-t border-slate-200 pt-2 dark:border-slate-700">
              <h3 className="mb-2 px-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                外部地图 API（高德 / Google）
              </h3>

              <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                <KpiCard chartId="map-api-today-paid-calls" />
                <KpiCard chartId="map-api-today-success-rate" />
                <KpiCard chartId="map-api-today-cache-hit-rate" />
                <KpiCard chartId="map-api-today-errors" />
              </div>

              {/* 配额水位表按「离撞线有多近」排序，最危险的排最前面 */}
              <div className="mt-2">
                <SimpleTableCard chartId="map-api-today-usage" />
              </div>

              {/* ── Google Maps 的钱 ──
                  GCP 的赠金/试用金不抵扣 Google Maps Platform，所以整张 GCP 账单里
                  只有这块是真金白银 —— 2026-08 的 28.61 SGD 全部来自这里，其他服务
                  （Compute Engine 158、Cloud SQL 55）当月都被赠金抵掉了。

                  免费额度按 SKU 算、自然月重置（Pro 5,000 次/月），超一次算一次钱，
                  所以「本月还剩多少额度」比「今天调了多少次」更能提前看见账单。 */}
              <div className="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-3">
                <KpiCard chartId="map-api-google-month-cost" />
                <div className="lg:col-span-2">
                  <SimpleTableCard chartId="map-api-google-month-cost-detail" />
                </div>
              </div>

              <div className="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-2">
                <MultiLineChartCard chartId="map-api-30-day-calls" title="30天调用量（按 provider）" />
                <MultiLineChartCard chartId="map-api-30-day-success-rate" title="30天成功率（按 provider）" />
              </div>
              <div className="mt-2">
                <LineChartCard chartId="map-api-30-day-error-trend" title="30天地图 API 错误趋势" />
              </div>
            </div>

            {/* 4 图表 2x2 网格 */}
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              <LineChartCard chartId="location-30-day-user-trend" title="30天每日设备数" />
              <MultiLineChartCard chartId="location-30-day-trend" title="30天调用趋势" />
              <LineChartCard chartId="location-30-day-error-trend" title="30天错误趋势" />
              <PieChartCard chartId="location-language-distribution" title="语言分布" />
            </div>

            {/* 请求详情表 */}
            <Card title="位置请求详情">
              <LocationDetailsTable />
            </Card>

            {/* 用户回传的原始数据样本，用于回放调优 */}
            <Card title="足迹导入文件">
              <FootprintImportUploadsTable />
            </Card>

            <Card title="TestFlight 诊断快照">
              <TestFlightDiagnosticsUploadsTable />
            </Card>
          </div>
        )}

        {activeTab === 'map' && (
          <div className="p-2">
            <LocationMap />
          </div>
        )}
      </div>
    </div>
  )
}
